import {
  Box,
  Button,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Divider,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuOption,
  Panel,
  PlusIcon,
  Skeleton,
  Text,
  toaster,
  Tooltip,
} from 'components'
import { usePanelProps } from 'components/Panel'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { useEffect, useRef, useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router'
import {
  createDoc,
  makeEmptyWorkspace,
  useEntevatePermission,
  useScrolling,
  useSwitchWorkspace,
  useTheme,
  useWorkspaceLogo,
  useWorkspaces,
} from 'utils'
import { useCurrentPage } from '../utils/pageUtils'
import { RoutePath } from '../utils/routing'
import { useSideMenuItems } from '../utils/sideMenuUtils'
import SideMenuButton from './SideMenuButton'

export default function SideMenu() {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  const [collapsed, setCollapsed] = useState(false)
  const sideMenuItems = useSideMenuItems()
  const { scrolling, overflowRef, contentRef } = useScrolling()

  const { hasWorkspaceLogo, workspaceIconSrc, workspaceLogoSrc } = useWorkspaceLogo()
  const pendingHotkeyPrefix = useRef(false)
  const hotkeyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleResize = () => setCollapsed(window.innerWidth < 768)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const toggleSideMenu = () => setCollapsed(s => !s)

  useEffect(() => {
    function resetPendingHotKey() {
      pendingHotkeyPrefix.current = false
      if (hotkeyTimerRef.current) {
        clearTimeout(hotkeyTimerRef.current)
        hotkeyTimerRef.current = null
      }
    }

    function handleModuleHotKeys(e: KeyboardEvent) {
      if (!workspaceId) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      const target = e.target as HTMLElement | null
      const tagName = target?.tagName?.toLowerCase()
      if (
        target?.isContentEditable ||
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select'
      )
        return

      const key = e.key.toLowerCase()
      if (!pendingHotkeyPrefix.current) {
        if (key !== 'g') return
        e.preventDefault()
        pendingHotkeyPrefix.current = true
        hotkeyTimerRef.current = setTimeout(() => {
          pendingHotkeyPrefix.current = false
          hotkeyTimerRef.current = null
        }, 1400)
        return
      }

      const targetItem = sideMenuItems.find(
        item => item.shortcutKey?.toLowerCase() === key && !item.disabled
      )
      resetPendingHotKey()
      if (!targetItem) return

      e.preventDefault()
      navigate({ pathname: generatePath(targetItem.page, { workspaceId, id: null }) })
    }

    document.addEventListener('keydown', handleModuleHotKeys)
    return () => {
      document.removeEventListener('keydown', handleModuleHotKeys)
      resetPendingHotKey()
    }
  }, [navigate, sideMenuItems, workspaceId])

  return (
    <Panel
      width={collapsed ? '3.75rem' : '12.5rem'}
      flexShrink={0}
      justify="space-between"
      my={3}
      overflow="hidden"
      hideBelow="md"
    >
      <Flex direction="column" overflow="hidden">
        <Flex mx={collapsed ? 3 : 5} my={5}>
          <Image
            alt="Icon"
            width={collapsed ? '100%' : undefined}
            maxHeight={collapsed ? '25px' : '50px'}
            objectFit="scale-down"
            src={collapsed ? workspaceIconSrc : workspaceLogoSrc}
          />
        </Flex>
        <WorkspaceMenu sideMenuCollapsed={collapsed} />
        <Flex
          overflow="auto"
          direction="column"
          ref={overflowRef}
          borderBottomWidth={scrolling ? '1px' : '0'}
          borderColor="border"
        >
          <Flex py={5} direction="column" ref={contentRef}>
            {sideMenuItems!.map((item, index) => (
              <SideMenuButton key={index} isCollapsed={collapsed} {...item} />
            ))}
          </Flex>
        </Flex>
      </Flex>

      <Flex my={2} alignSelf="center" direction="row" align="center">
        {!collapsed && !hasWorkspaceLogo ? (
          <Button
            aria-label="Collapse"
            leftIcon={<ChevronDoubleLeftIcon />}
            size="sm"
            onClick={toggleSideMenu}
            opacity={theme === 'light' ? 0.7 : 1}
            variant="ghost"
          >
            Collapse
          </Button>
        ) : (
          <Tooltip content={collapsed ? 'Expand' : 'Collapse'} positioning={{ placement: 'top' }}>
            <IconButton
              aria-label={collapsed ? 'Expand' : 'Collapse'}
              size="sm"
              icon={collapsed ? <ChevronDoubleRightIcon /> : <ChevronDoubleLeftIcon />}
              opacity={theme === 'light' ? 0.7 : 1}
              onClick={toggleSideMenu}
            />
          </Tooltip>
        )}
        {!collapsed && hasWorkspaceLogo && (
          <>
            <Divider mx={2} orientation="vertical" />
            <Flex
              cursor="pointer"
              direction="column"
              align="flex-start"
              opacity={theme === 'light' ? 0.7 : 1}
            >
              <Text fontSize="10px">powered by</Text>
              <Image
                src={
                  theme === 'light'
                    ? '/common/momentify-logo-dark.svg'
                    : '/common/momentify-logo-light.svg'
                }
                height="14px"
              />
            </Flex>
          </>
        )}
      </Flex>
    </Panel>
  )
}

interface WorkspaceMenuProps {
  sideMenuCollapsed: boolean
}

function WorkspaceMenu({ sideMenuCollapsed }: WorkspaceMenuProps) {
  const user = useCurrentUser()
  const navigate = useNavigate()
  const { workspace } = useWorkspace()
  const { workspaces, refetch } = useWorkspaces('emp')
  const { workspaceId } = useParams()
  const { theme } = useTheme()
  const switchWorkspace = useSwitchWorkspace()
  const [entevatePermission] = useEntevatePermission()
  const panelProps = usePanelProps()

  const currentPage = useCurrentPage()
  const currentPagePath = currentPage?.isChild ? currentPage.parentPath : currentPage?.path
  const workspacesList = [{ id: 'all', title: 'All' }, ...(workspaces ?? [])]

  async function handleNewWorkspace() {
    const doc = await createDoc(user?.id!, 'Workspace', makeEmptyWorkspace('emp'))
    toaster.create({
      title: 'Workspace created',
      type: 'success',
      description: 'Navigating to new workspace',
    })
    refetch()
    navigate(generatePath(RoutePath.ManageWorkspace, { workspaceId: doc.id }))
  }

  let menuOptions: MenuOption<string>[] = [
    {
      type: 'custom',
      label: (
        <Text textStyle="bodySmall" opacity={0.6} pl={4} py={2}>
          Workspaces
        </Text>
      ),
    },
    {
      type: 'group',
      overflow: 'auto',
      maxHeight: '300px',
      borderYWidth: '1px',
      options: workspacesList.map(({ id, title }) => ({
        label: (
          <Text
            textStyle="bodySmall"
            fontStyle={id === 'all' ? 'italic' : undefined}
            opacity={title ? 1 : 0.5}
            as={title ? 'p' : 'i'}
            pr={3}
          >
            {title || 'Draft'}
          </Text>
        ),
        value: id,
      })),
    },
  ]

  if (entevatePermission === 'entevate')
    menuOptions.push({
      type: 'custom',
      label: (
        <Box p={2}>
          <Button w="100%" leftIcon={<PlusIcon />} onClick={handleNewWorkspace} variant="ghost">
            Create New
          </Button>
        </Box>
      ),
    })

  const menuTrigger = (
    <Flex
      backdropFilter="none"
      borderRadius="none"
      direction="column"
      display="flex"
      flex={1}
      overflow="hidden"
    >
      {!sideMenuCollapsed && (
        <Text
          textStyle={'bodyXSmall'}
          color={theme === 'light' ? 'rgba(23, 23, 23, 0.4)' : 'rgba(255, 255, 255, 0.5)'}
          textAlign={'left'}
        >
          Workspace
        </Text>
      )}
      {workspace || workspaceId === 'all' ? (
        <Text textStyle="bodyXSmall" textAlign="left" truncate textOverflow="ellipsis">
          {workspace ? workspace.title : 'All'}
        </Text>
      ) : (
        <Skeleton height="18px" />
      )}
    </Flex>
  )

  if (workspaces?.length == 1)
    return (
      <Panel mx={3} p={2} px={3}>
        {menuTrigger}
      </Panel>
    )

  return (
    <Menu
      {...(((workspaces?.length ?? 0) < 2 || sideMenuCollapsed) && { rightIcon: undefined })}
      buttonLabel={menuTrigger}
      height="auto"
      contentProps={{ p: 0 }}
      menuProps={{ positioning: { placement: 'bottom-start' } }}
      onChange={id => switchWorkspace(id as string, currentPagePath)}
      options={menuOptions}
      portal
      mx={3}
      px={4}
      py={2}
      value={workspaceId}
      {...(panelProps as any)}
      border="none"
    />
  )
}
